// src/components/Browser/FaviconImage.js
import React, { useState, useEffect } from 'react';

/**
 * A component that tries different favicon formats and falls back gracefully
 * 
 * @param {Object} props 
 * @param {string} props.hostname - The hostname to fetch favicon for
 * @param {string} props.className - Optional class name for styling
 * @param {string} props.alt - Alt text for the image
 * @returns {JSX.Element} - React component
 */
const FaviconImage = ({ hostname, className, alt }) => {
  const [currentSrc, setCurrentSrc] = useState(null);
  const [failedUrls, setFailedUrls] = useState([]);
  
  // List of potential favicon URLs to try in order
  const faviconUrls = [
    `https://${hostname}/favicon.ico`,
    `https://${hostname}/favicon.png`, 
    `https://${hostname}/apple-touch-icon.png`,
    `https://${hostname}/apple-touch-icon-precomposed.png`,
    `https://www.google.com/s2/favicons?domain=${hostname}`,
    '/assets/wallet-logo.png' // Final fallback is our wallet logo
  ];
  
  useEffect(() => {
    // Reset when hostname changes
    setFailedUrls([]);
    setCurrentSrc(faviconUrls[0]);
  }, [hostname]);
  
  // Handle error by trying the next URL in the sequence
  const handleError = () => {
    const nextIndex = failedUrls.length + 1;
    
    // If we've tried all options, stay with the last one (wallet logo)
    if (nextIndex >= faviconUrls.length) {
      return;
    }
    
    setFailedUrls([...failedUrls, currentSrc]);
    setCurrentSrc(faviconUrls[nextIndex]);
  };
  
  if (!hostname) {
    return <img src="/assets/wallet-logo.png" alt={alt || 'Site icon'} className={className} />;
  }
  
  return (
    <img 
      src={currentSrc}
      alt={alt || hostname}
      className={className}
      onError={handleError}
    />
  );
};

export default FaviconImage;